import * as uiAnalyticsConsumer from '@servicenow/ui-analytics-consumer';
import getStatePropsContext from './getStatePropsContext';
import {createInteractionId, mark, types as metrics} from '@servicenow/ui-metrics';
import {produce} from 'immer';
import {isValidOperation, isBatch, isUpdater} from './utils';
import * as operations from '../operators/operations';
import getHostVariables from '../getHostVariables';
import {schedule, types} from '../../scheduler';
import deferHelperExecution from './deferHelperExecution';
import {
	HELPERS_SCHEDULED,
	SCHEDULE_IMMEDIATELY_DO_NOT_USE_OR_YOU_WILL_BE_IMMEDIATELY_FIRED,
	HELPERS_DEFER,
	SPAN_FOR_UPDATE_CONTEXT,
	SPAN_KIND_CLIENT,
	SPAN_ATTR_UPDATE_CONTEXT_SHOULD_BATCH,
	SPAN_ATTR_DID_UPDATE,
	SPAN_ATTR_SHOULD_RENDER
} from '../../constants';
import log from '../../log';
import renderContextConsumers from '../../context/renderContextConsumers';
import {getContext, setContext} from '../../context/contexts';
import {getSpanOptions} from '../';
const {trace} = uiAnalyticsConsumer;
const {IMMEDIATE, STATE_SET} = types;
const {
	declarativeOperations: {ASSIGN}
} = operations;

const resolveUpdate = (host, hostVariables, update, interactionId) => {
	if (!isUpdater(update)) return update;

	const statePropsContext = getStatePropsContext(host, interactionId);
	return update(statePropsContext);
};

function handleContextUpdate(host, hostVariables, update, interactionId) {
	const resolvedUpdate = resolveUpdate(host, hostVariables, update, interactionId);

	if (!resolvedUpdate) return {didUpdate: false, shouldRender: false};

	const {context: contextKey} = resolvedUpdate;
	const previousContext = getContext(contextKey);
	const {
		shouldUpdate = true,
		operation = previousContext !== null && typeof previousContext === 'object' ? ASSIGN : '',
		path = '',
		start,
		deleteCount,
		addItems,
		shouldRender = true,
		value
	} = resolvedUpdate;

	if (!shouldUpdate) return {didUpdate: false, shouldRender: false};

	const nextContext = produce(getContext(contextKey) || {}, draftState => {
		if (!operation) return value;

		if (!isValidOperation(operation)) {
			log(`Invalid update context operation: ${operation}`, {host, origin: 'handleContextUpdate'});
			return;
		}

		try {
			operations[operation](draftState, {
				path,
				value,
				start,
				deleteCount,
				addItems: Array.isArray(addItems) ? addItems : []
			});
		} catch (error) {
			log('Error while performing context update', {error, host, origin: 'handleContextUpdate'});
		}
	});

	if (nextContext === previousContext) return {didUpdate: false, shouldRender: false};

	setContext(contextKey, nextContext);
	return {didUpdate: true, shouldRender};
}

function handleContextUpdateBatch(host, hostVariables, batch, interactionId) {
	return batch.reduce(
		(acc, update) => {
			const {shouldRender, didUpdate} = handleContextUpdate(
				host,
				hostVariables,
				update,
				interactionId
			);

			const result = {...acc};
			if (didUpdate) result.didUpdate = true;

			if (shouldRender) result.shouldRender = true;

			return result;
		},
		{didUpdate: false, shouldRender: false}
	);
}

const resolveUpdateForMark = update => (update === 'function' ? 'FUNCTION' : update);
const transformUpdateForMark = (update, isBatch) => {
	if (isBatch) return update.map(resolveUpdateForMark);

	return resolveUpdateForMark(update);
};

export default function createUpdateContext(host, executionTiming) {
	const hostVariables = getHostVariables(host);

	return function updateContext(update, options = {}) {
		const interactionId = options.interactionId || createInteractionId();
		const activeSpan = options.span;
		if (executionTiming === HELPERS_DEFER)
			mark(host, interactionId, metrics.DEFER, {deferred: metrics.UPDATE_CONTEXT});
		const meta = {el: host, interactionId, span: activeSpan};
		deferHelperExecution(
			executionTiming,
			() => {
				// TODO: Remove this immediate option for a better scheduling alternative
				// https://code.devsnc.com/gist/dylan-mozlowski/07bf0ab13b2c8a12f0cc6e59bf4cf911
				const {
					[SCHEDULE_IMMEDIATELY_DO_NOT_USE_OR_YOU_WILL_BE_IMMEDIATELY_FIRED]:
						optionallyImmediate = false
				} = options;
				const updateContextSpan = trace(SPAN_FOR_UPDATE_CONTEXT, {
					...getSpanOptions(host),
					traceId: interactionId,
					kind: SPAN_KIND_CLIENT,
					parentSpan: activeSpan
				});
				schedule({
					fn() {
						const shouldBatch = isBatch(update);
						const transformedUpdate = transformUpdateForMark(update, shouldBatch);
						mark(host, interactionId, metrics.UPDATE_CONTEXT, {
							update: transformedUpdate
						});
						updateContextSpan.setAttribute(SPAN_ATTR_UPDATE_CONTEXT_SHOULD_BATCH, shouldBatch);
						const updateHandler = shouldBatch ? handleContextUpdateBatch : handleContextUpdate;
						const {didUpdate, shouldRender} = updateHandler(
							host,
							hostVariables,
							update,
							interactionId
						);
						if (!didUpdate) {
							updateContextSpan.setAttribute(SPAN_ATTR_DID_UPDATE, false);
							updateContextSpan.end();
							return;
						}

						if (shouldRender) {
							const contexts = shouldBatch ? update.map(({context}) => context) : [update.context];
							for (let i = 0; i < contexts.length; i++) {
								renderContextConsumers(contexts[i], interactionId, {
									update: transformUpdateForMark(update, shouldBatch),
									activeSpan: updateContextSpan
								});
							}
						} else {
							updateContextSpan.setAttribute(SPAN_ATTR_SHOULD_RENDER, false);
						}
						updateContextSpan.end();
					},
					meta: {...meta, span: updateContextSpan},
					type: executionTiming !== HELPERS_SCHEDULED || optionallyImmediate ? IMMEDIATE : STATE_SET
				});
			},
			meta
		);
	};
}
